import React, { useState, useEffect } from 'react';
import { MegaphoneIcon, PlusIcon } from '@heroicons/react/24/outline';
import { fetchCampaigns, createCampaign } from '../../services/user';
import toast from 'react-hot-toast';

const Campaigns = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    const [formData, setFormData] = useState({ name: '', description: '', budget: 0, startDate: '', endDate: '' });

    const userId = localStorage.getItem('ud-user') || '';

    const loadCampaigns = async () => {
        setIsLoading(true);
        try {
            const data = await fetchCampaigns(userId);
            setCampaigns(data);
        } catch(err) {
            toast.error("Failed to load campaigns");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadCampaigns();
    }, []);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await createCampaign({ ...formData, userId });
            toast.success("Campaign created successfully!");
            setIsModalOpen(false);
            setFormData({ name: '', description: '', budget: 0, startDate: '', endDate: '' });
            loadCampaigns();
        } catch(err: any) {
            toast.error("Failed to create campaign");
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Campaign Designer</h1>
                    <p className="text-gray-600">Create and manage marketing campaigns</p>
                </div>
                <button 
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    New Campaign
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {isLoading ? (
                    <div className="col-span-full text-center py-8 text-gray-500">Loading campaigns...</div>
                ) : campaigns.length === 0 ? (
                    <div className="col-span-full text-center py-12 bg-white rounded-lg border border-dashed border-gray-300">
                        <MegaphoneIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns</h3>
                        <p className="mt-1 text-sm text-gray-500">Get started by creating a new marketing campaign.</p>
                    </div>
                ) : (
                    campaigns.map((c: any) => (
                        <div key={c._id} className="bg-white shadow rounded-lg p-6 flex flex-col">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-lg font-bold text-gray-900">{c.name}</h3>
                                <span className={`px-2 py-1 text-xs rounded-full ${c.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                    {c.status}
                                </span>
                            </div>
                            <p className="text-sm text-gray-600 flex-1 mb-4">{c.description}</p>
                            
                            {/* Analytics Section */}
                            {c.analytics && (
                                <div className="bg-gray-50 rounded p-3 mb-4 grid grid-cols-3 gap-2 text-center text-xs divide-x">
                                    <div>
                                        <div className="text-gray-500 mb-1">Reach</div>
                                        <div className="font-bold text-gray-900">{c.analytics.totalReach}</div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 mb-1">Orders</div>
                                        <div className="font-bold text-gray-900">{c.analytics.totalOrders}</div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 mb-1">Revenue</div>
                                        <div className="font-bold text-green-600">₹{c.analytics.totalRevenue.toFixed(0)}</div>
                                    </div>
                                </div>
                            )}

                            <div className="border-t pt-4 flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-500">Budget: ₹{c.budget}</span>
                                <span className="text-xs text-gray-400">{new Date(c.createdAt).toLocaleDateString()}</span>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Create Campaign Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Draft New Campaign</h2>
                        <form onSubmit={handleCreate}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Campaign Name</label>
                                    <input required type="text" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Description</label>
                                    <textarea className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Budget (₹)</label>
                                    <input required type="number" min="0" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" value={formData.budget} onChange={e => setFormData({...formData, budget: Number(e.target.value)})} />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Start Date</label>
                                        <input required type="date" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" value={formData.startDate} onChange={e => setFormData({...formData, startDate: e.target.value})} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">End Date</label>
                                        <input type="date" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" value={formData.endDate} onChange={e => setFormData({...formData, endDate: e.target.value})} />
                                    </div>
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end space-x-3">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Campaigns;
